/**
 * Export Excel riche et formaté de la liste des membres.
 *
 * Génère un classeur avec deux feuilles :
 *  - « Membres »  : fiche complète de chaque membre (identité, contact, rôle,
 *                   statut) + finances (Sass annuel, cotisé, reste à payer,
 *                   progression) + détail mensuel Jan→Déc, avec en-tête figé,
 *                   filtres automatiques et ligne de totaux.
 *  - « Synthèse » : statistiques globales et répartition par catégorie Sass.
 *
 * Le formatage (couleurs, gras, bordures, formats nombre) repose sur ExcelJS,
 * la version communautaire de SheetJS (`xlsx`) ne sachant pas styliser.
 */

// ---- Palette ----
const NAVY = 'FF0B1F3A';      // bandeau titre
const GREEN = 'FF0F5132';     // en-tête de tableau
const GREEN_LIGHT = 'FFE7F1EC';
const ZEBRA = 'FFF5F7FA';     // ligne paire
const GREY_BORDER = 'FFD9DEE5';
const AMBER = 'FF9A6700';
const RED = 'FFB42318';
const SLATE = 'FF475467';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const ROLE_LABELS = { admin: 'Administrateur', responsable_commission: 'Responsable Commission', membre: 'Membre' };
const APPROVAL_LABELS = { true: 'Validé', pending: 'En attente', false: 'Refusé' };

const MONEY_FMT = '#,##0" FCFA"';
const PCT_FMT = '0"%"';

const thin = { style: 'thin', color: { argb: GREY_BORDER } };
const allBorders = { top: thin, left: thin, bottom: thin, right: thin };

const toJsDate = (v) => {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

const fmtDate = (v) => {
  const d = toJsDate(v);
  return d ? d.toLocaleDateString('fr-FR') : '';
};

/**
 * Construit le classeur et déclenche son téléchargement.
 * @param {Array} members  Liste des membres (déjà enrichie de `monthly` et `annualTotal`).
 * @param {object} opts
 * @param {object} opts.settings         Réglages généraux (annualGoal de repli).
 * @param {Array}  opts.sassCategories   Catégories Sass configurées.
 * @returns {Promise<number>} Nombre de membres exportés.
 */
export async function exportMembersToExcel(members, { settings = {}, sassCategories = [] } = {}) {
  // exceljs est volumineux : chargé à la demande pour rester hors du bundle principal.
  const ExcelJS = (await import('exceljs')).default;
  const sassLabel = (id) => sassCategories.find((c) => c.id === id)?.label || '';
  const defaultGoal = settings.annualGoal || 100000;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'Daara Wakana';
  wb.created = new Date();

  buildMembersSheet(wb, members, { sassLabel, defaultGoal });
  buildSummarySheet(wb, members, { sassCategories, defaultGoal });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `membres_daara_wakana_${new Date().toISOString().split('T')[0]}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return members.length;
}

// ---- Feuille « Membres » ----

function buildMembersSheet(wb, members, { sassLabel, defaultGoal }) {
  const ws = wb.addWorksheet('Membres', {
    views: [{ state: 'frozen', xSplit: 2, ySplit: 4 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });

  const columns = [
    { header: '#', key: 'idx', width: 5 },
    { header: 'Nom complet', key: 'fullName', width: 26 },
    { header: 'Email', key: 'email', width: 28 },
    { header: 'Téléphone', key: 'phone', width: 14 },
    { header: 'Matricule', key: 'matricule', width: 14 },
    { header: 'Adresse', key: 'address', width: 24 },
    { header: 'Rôle', key: 'role', width: 22 },
    { header: 'Commission', key: 'commission', width: 18 },
    { header: 'Catégorie Sass', key: 'sass', width: 16 },
    { header: 'Statut', key: 'status', width: 10 },
    { header: 'Validation', key: 'approval', width: 12 },
    { header: 'Membre depuis', key: 'joined', width: 15 },
    { header: 'Sass annuel', key: 'goal', width: 15, money: true },
    { header: 'Cotisé', key: 'paid', width: 15, money: true },
    { header: 'Reste à payer', key: 'left', width: 15, money: true },
    { header: 'Progression', key: 'progress', width: 12, pct: true },
    ...MONTHS.map((m) => ({ header: m, key: `m_${m}`, width: 12, money: true }))
  ];
  const lastCol = columns.length;

  // Bandeau titre (lignes 1-3)
  ws.mergeCells(1, 1, 1, lastCol);
  const title = ws.getCell(1, 1);
  title.value = 'DAARA WAKANA — Liste des membres';
  title.font = { name: 'Calibri', size: 18, bold: true, color: { argb: 'FFFFFFFF' } };
  title.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(1).height = 30;

  ws.mergeCells(2, 1, 2, lastCol);
  const sub = ws.getCell(2, 1);
  const actifs = members.filter((m) => m.isActive !== false).length;
  sub.value = `${members.length} membre(s) · ${actifs} actif(s) · Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  sub.font = { name: 'Calibri', size: 11, color: { argb: 'FFC7D2E0' } };
  sub.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(2).height = 20;

  for (let r = 1; r <= 2; r++) {
    for (let c = 1; c <= lastCol; c++) {
      ws.getCell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    }
  }
  ws.getRow(3).height = 6; // respiration

  // En-tête de tableau (ligne 4)
  const headerRowIdx = 4;
  const headerRow = ws.getRow(headerRowIdx);
  columns.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = col.header;
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = allBorders;
  });
  headerRow.height = 26;

  // Lignes de données
  let row = headerRowIdx + 1;
  members.forEach((m, i) => {
    const goal = m.annualGoal || defaultGoal;
    const paid = m.annualTotal || 0;
    const left = Math.max(goal - paid, 0);
    const progress = goal > 0 ? Math.round(Math.min((paid / goal) * 100, 100)) : 0;
    const monthly = m.monthly || Array(12).fill(0);

    const values = [
      i + 1,
      m.fullName || '',
      m.email || '',
      m.phone || '',
      m.matricule || '',
      m.address || '',
      ROLE_LABELS[m.role] || m.role || 'Membre',
      m.commission || '',
      sassLabel(m.sassCategory),
      m.isActive === false ? 'Inactif' : 'Actif',
      APPROVAL_LABELS[String(m.isApproved)] || 'Validé',
      fmtDate(m.joinedAt),
      goal,
      paid,
      left,
      progress,
      ...monthly
    ];

    const r = ws.getRow(row);
    values.forEach((v, c) => {
      const cell = r.getCell(c + 1);
      cell.value = v;
      cell.border = allBorders;
    });

    // Formats nombre
    columns.forEach((col, c) => {
      const cell = r.getCell(c + 1);
      if (col.money) cell.numFmt = MONEY_FMT;
      if (col.pct) cell.numFmt = PCT_FMT;
    });

    // Alignements spécifiques
    r.getCell(1).alignment = { horizontal: 'center' };
    r.getCell(10).alignment = { horizontal: 'center' }; // Statut
    r.getCell(11).alignment = { horizontal: 'center' }; // Validation
    r.getCell(12).alignment = { horizontal: 'center' }; // Membre depuis
    r.getCell(16).alignment = { horizontal: 'center' }; // Progression

    // Couleur statut / validation
    const statusCell = r.getCell(10);
    statusCell.font = { color: { argb: m.isActive === false ? SLATE : GREEN }, bold: true };
    const approvalCell = r.getCell(11);
    if (String(m.isApproved) === 'pending') approvalCell.font = { color: { argb: AMBER }, bold: true };
    else if (String(m.isApproved) === 'false') approvalCell.font = { color: { argb: RED }, bold: true };

    // Couleur progression
    const progressCell = r.getCell(16);
    progressCell.font = { bold: true, color: { argb: progress >= 100 ? GREEN : progress >= 50 ? AMBER : RED } };

    // Reste à payer en rouge si > 0
    if (left > 0) r.getCell(15).font = { color: { argb: RED } };

    // Zébrures
    if (i % 2 === 1) {
      for (let c = 1; c <= lastCol; c++) {
        const cell = r.getCell(c);
        if (!cell.fill || cell.fill.type !== 'pattern') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
        }
      }
    }
    row++;
  });

  // Ligne de totaux
  const totalRow = ws.getRow(row);
  totalRow.getCell(1).value = '';
  const labelCell = totalRow.getCell(2);
  labelCell.value = 'TOTAUX';
  for (let c = 1; c <= 12; c++) {
    totalRow.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
  }
  labelCell.font = { bold: true, color: { argb: GREEN } };

  const sumOf = (selector) => members.reduce((s, m) => s + selector(m), 0);
  const totalGoal = sumOf((m) => m.annualGoal || defaultGoal);
  const totalPaid = sumOf((m) => m.annualTotal || 0);
  const totalLeft = Math.max(totalGoal - totalPaid, 0);
  const globalPct = totalGoal > 0 ? Math.round((totalPaid / totalGoal) * 100) : 0;
  const monthTotals = MONTHS.map((_, mi) => sumOf((m) => (m.monthly || [])[mi] || 0));

  const totals = { 13: totalGoal, 14: totalPaid, 15: totalLeft, 16: globalPct };
  monthTotals.forEach((v, mi) => { totals[17 + mi] = v; });

  for (let c = 1; c <= lastCol; c++) {
    const cell = totalRow.getCell(c);
    cell.border = { top: { style: 'medium', color: { argb: GREEN } }, bottom: thin, left: thin, right: thin };
    if (totals[c] !== undefined) {
      cell.value = totals[c];
      cell.font = { bold: true, color: { argb: GREEN } };
      cell.numFmt = c === 16 ? PCT_FMT : MONEY_FMT;
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
    }
  }
  totalRow.height = 22;

  // Filtres automatiques sur la zone de tableau
  ws.autoFilter = {
    from: { row: headerRowIdx, column: 1 },
    to: { row: headerRowIdx, column: lastCol }
  };
}

// ---- Feuille « Synthèse » ----

function buildSummarySheet(wb, members, { sassCategories, defaultGoal }) {
  const ws = wb.addWorksheet('Synthèse', {
    pageSetup: { orientation: 'portrait' }
  });
  ws.columns = [{ width: 34 }, { width: 20 }, { width: 18 }, { width: 18 }];

  ws.mergeCells(1, 1, 1, 4);
  const title = ws.getCell(1, 1);
  title.value = 'Synthèse — Daara Wakana';
  title.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
  title.alignment = { vertical: 'middle', indent: 1 };
  for (let c = 1; c <= 4; c++) ws.getCell(1, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
  ws.getRow(1).height = 28;

  const total = members.length;
  const actifs = members.filter((m) => m.isActive !== false).length;
  const inactifs = total - actifs;
  const enAttente = members.filter((m) => String(m.isApproved) === 'pending').length;
  const admins = members.filter((m) => m.role === 'admin').length;
  const totalGoal = members.reduce((s, m) => s + (m.annualGoal || defaultGoal), 0);
  const totalPaid = members.reduce((s, m) => s + (m.annualTotal || 0), 0);
  const tauxGlobal = totalGoal > 0 ? Math.round((totalPaid / totalGoal) * 100) : 0;

  const sectionHeader = (label, rowIdx) => {
    ws.mergeCells(rowIdx, 1, rowIdx, 4);
    const cell = ws.getCell(rowIdx, 1);
    cell.value = label;
    cell.font = { bold: true, size: 12, color: { argb: GREEN } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
    cell.alignment = { vertical: 'middle', indent: 1 };
    ws.getRow(rowIdx).height = 22;
  };

  const kv = (label, value, rowIdx, money = false, pct = false) => {
    const l = ws.getCell(rowIdx, 1);
    l.value = label;
    l.font = { color: { argb: SLATE } };
    l.border = { bottom: thin };
    const v = ws.getCell(rowIdx, 2);
    v.value = value;
    v.font = { bold: true };
    if (money) v.numFmt = MONEY_FMT;
    if (pct) v.numFmt = PCT_FMT;
    v.border = { bottom: thin };
  };

  let r = 3;
  sectionHeader('Effectifs', r++);
  kv('Total membres', total, r++);
  kv('Membres actifs', actifs, r++);
  kv('Membres inactifs', inactifs, r++);
  kv('En attente de validation', enAttente, r++);
  kv('Administrateurs', admins, r++);

  r++;
  sectionHeader('Finances (année en cours)', r++);
  kv('Objectif Sass cumulé', totalGoal, r++, true);
  kv('Total cotisé', totalPaid, r++, true);
  kv('Reste à collecter', Math.max(totalGoal - totalPaid, 0), r++, true);
  kv('Taux de réalisation global', tauxGlobal, r++, false, true);

  r++;
  sectionHeader('Répartition par catégorie Sass', r);
  // L'en-tête de tableau est placé juste après le titre de section
  r++;
  const catHeader = ws.getRow(r);
  ['Catégorie', 'Membres', 'Objectif', 'Cotisé'].forEach((h, i) => {
    const cell = catHeader.getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN } };
    cell.alignment = { horizontal: i === 0 ? 'left' : 'center', indent: i === 0 ? 1 : 0 };
    cell.border = allBorders;
  });
  r++;

  const cats = [...sassCategories.map((c) => ({ id: c.id, label: c.label })), { id: '__none__', label: 'Non catégorisé' }];
  cats.forEach((cat) => {
    const list = members.filter((m) => (cat.id === '__none__' ? !m.sassCategory : m.sassCategory === cat.id));
    if (list.length === 0 && cat.id === '__none__') return;
    const objectif = list.reduce((s, m) => s + (m.annualGoal || defaultGoal), 0);
    const cotise = list.reduce((s, m) => s + (m.annualTotal || 0), 0);
    const cr = ws.getRow(r);
    cr.getCell(1).value = cat.label;
    cr.getCell(2).value = list.length;
    cr.getCell(3).value = objectif;
    cr.getCell(4).value = cotise;
    cr.getCell(2).alignment = { horizontal: 'center' };
    cr.getCell(3).numFmt = MONEY_FMT;
    cr.getCell(4).numFmt = MONEY_FMT;
    for (let c = 1; c <= 4; c++) cr.getCell(c).border = allBorders;
    cr.getCell(1).alignment = { indent: 1 };
    r++;
  });
}
